import React, { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import weekday from 'dayjs/plugin/weekday';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { get_leaveRequest_with_pagination } from '../../../../config/ManagementApis/LeaveRequestApis';
import CustomSelectDropdown from '../../../../Components/custom_components/CustomSelectDropdown/CustomSelectDropdown';
import ActionButton from '../../../../Components/custom_components/CustomButtons/ActionButton/ActionButton';
import './LeaveCalendar.css';
import TableSkeleton from '../../../../Components/Skeletons/TableSkeleton';
import { monthsList, yearsList } from '../../../../utils/formatDate';
dayjs.extend(isSameOrBefore);
dayjs.extend(weekday);
dayjs.extend(customParseFormat); // Extend here

// FOR GET CODE AND COLOR OF THE LEAVE
const leaveMap = {
    'Sick Leave': { code: 'SL', bgColor: '#2196F3' },
    'Casual Leave': { code: 'CL', bgColor: '#FF9800' },
    'Earned Leave': { code: 'EL', bgColor: '#4CAF50' },
    // 'Leave without Pay': { code: 'LWP', bgColor: '#F44336' },
    'Maternity Leave': { code: 'ML', bgColor: '#9C27B0' },
    // 'Wedding Leave': { code: 'WL', bgColor: '#E91E63' },
    'Compensatory leave': { code: 'CPL', bgColor: '#607D8B' },
};
// for legend
const LeaveLegend = () => {
    const combinedMap = { ...leaveMap, ...dynamicLeaveMap };
    return (
        <div className="leave-legend" style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
            {Object.entries(combinedMap).map(([leaveType, { code, bgColor }]) => (
                <div key={leaveType} className="legend-item" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <div
                        className="legend-color-box"
                        style={{
                            backgroundColor: bgColor,
                            color: '#fff',
                            fontWeight: 'bold',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            minWidth: '40px',
                            textAlign: 'center',
                        }}
                    >
                        {code}
                    </div>
                    <span>{leaveType}</span>
                </div>
            ))}
        </div>
    );
};
// Store dynamically generated colors 
const dynamicLeaveMap = {};

const getLeaveColorCode = (type) => {
    if (!type) {
        return { code: '', bgColor: '#ccc' }; // Default code and grey background for undefined
    }
    // If already in static map
    if (leaveMap[type]) return leaveMap[type];

    // If already dynamically generated earlier
    if (dynamicLeaveMap[type]) return dynamicLeaveMap[type];

    // Generate random color
    const randomColor = getRandomColor();
    const code = generateCodeFromType(type);

    dynamicLeaveMap[type] = { code, bgColor: randomColor };
    return dynamicLeaveMap[type];
};

// GENERATE SHORT CODE FOR THR LRAVE TYPE
const generateCodeFromType = (type) => {
    return type
        ?.split(' ')
        ?.map(word => word[0])
        ?.join('')
        ?.toUpperCase(); // eg: 'Leave without Pay' => LWP
};

// FOR GET RANDOM COLOR FO THE LEAVE TYPE
const getRandomColor = () => {
    const letters = '0123456789ABCDEF';
    let color = '#';
    for (let i = 0; i < 6; i++) {
        color += letters[Math.floor(Math.random() * 16)];
    }
    return color;
};

// get dates of the month
const getMonthDates = (year, month) => {
    const start = dayjs(`${year}-${month}-01`);
    const end = start.endOf('month');
    const dates = [];
    let current = start;

    while (current.isSameOrBefore(end, 'day')) {
        dates.push(current.format('YYYY-MM-DD'));
        current = current.add(1, 'day');
    }

    return dates;
};

const LeaveTracker = () => {

    // Initialize with current values
    const currentYear = new Date().getFullYear();
    const currentMonth = String(new Date().getMonth() + 1).padStart(2, '0');

    const [selectedYear, setSelectedYear] = useState(currentYear.toString());
    const [selectedMonth, setSelectedMonth] = useState(currentMonth);

    const [leaveRequests, setLeaveRequests] = useState([]);

    const [isLoading, setIsLoading] = useState(false); // Loader state

    const monthDates = useMemo(() => getMonthDates(selectedYear, selectedMonth), [selectedYear, selectedMonth]);

    const employees = useMemo(() => {
        return [...new Set(leaveRequests?.map(l => l.EmployeeName))];
    }, [leaveRequests]);

    // Fetch leave requests
    const fetchEmployeeLeaves = async () => {
        try {
            const params = {
                month: selectedMonth,
                year: selectedYear,
                status: 1 // Approved only

            };
            setIsLoading(true)
            const res = await get_leaveRequest_with_pagination(params);
            if (res?.status === 'success') {
                setLeaveRequests(res.data?.data || []);
            } else {
                setLeaveRequests([]);
            }
        } catch (error) {
            console.error("Leave fetch error", error);
            setLeaveRequests([]);
        } finally {
            setIsLoading(false)
        }
    };

    useEffect(() => {
        fetchEmployeeLeaves();
    }, [selectedMonth, selectedYear]);

    const leaveMap = useMemo(() => {
        const map = {};
        leaveRequests?.forEach(l => {
            const from = dayjs(l.LeaveFrom, 'DD-MM-YYYY');
            const to = dayjs(l.LeaveTo, 'DD-MM-YYYY');
            let curr = from;

            while (curr.isSameOrBefore(to, 'day')) {
                if (curr.format('YYYY-MM') === `${selectedYear}-${selectedMonth}`) {
                    const dateStr = curr.format('YYYY-MM-DD');
                    map[`${l.EmployeeName}-${dateStr}`] = l.LeaveType;
                }
                curr = curr.add(1, 'day');
            }
        });
        return map;
    }, [selectedMonth, selectedYear, leaveRequests]);


    // /RESET AFTER CLICK RESET BUTTON
    const handleReset = () => {
        setSelectedMonth(currentMonth);
        setSelectedYear(currentYear.toString());
    };

    return (
        <div className='leave-tracker-container'>
            <div className="leave-tracker-filters">
                <div>
                    {/* <label>Year: </label> */}
                    {/* <CustomSelectDropdown
                        name="year"
                        placeholder="Select Year"
                        options={years?.map((yr) => ({ label: yr, value: yr }))}
                        value={{ label: selectedYear, value: selectedYear }}
                        onChange={(selectedOption) => {
                            setSelectedYear(selectedOption?.value);
                        }}
                        isMulti={false}
                        isClearable={false}
                        width="200px"
                    /> */}

                    <CustomSelectDropdown
                        name="year"
                        placeholder="Select Year"
                        options={yearsList} // Reusable array
                        value={
                            yearsList.find((yr) => yr.value === selectedYear)
                        }
                        onChange={(selectedOption) => {
                            setSelectedYear(selectedOption?.value);
                        }}
                        isMulti={false}
                        isClearable={false}
                        width="200px"
                    />
                </div>
                <div>
                    {/* <label>Month: </label> */}
                    <CustomSelectDropdown
                        name="month"
                        placeholder="Select Month"
                        options={monthsList} // Use reusable month list
                        value={
                            monthsList.find((opt) => opt.value === selectedMonth) ||
                            monthsList[0] // optional fallback (January or "All" if using monthOptions)
                        }
                        onChange={(selectedOption) => {
                            setSelectedMonth(selectedOption?.value || '');
                        }}
                        isMulti={false}
                        isClearable={false}
                        width="200px"
                    />
                </div>
                <ActionButton
                    label="Reset"
                    btnType="outlined"
                    onClick={handleReset}
                />
                <LeaveLegend />
            </div>
            {isLoading ? <TableSkeleton numColumns={28} numRows={5} /> : (
                <table className="leave-tracker-table">
                    <thead>
                        <tr style={{ backgroundColor: 'rgb(86 124 9)', color: '#fff' }}>
                            <th style={{ padding: 8, position: 'sticky', left: -10, backgroundColor: 'rgb(86 124 9)' }}>Employee</th>
                            {monthDates.map(date => (
                                <th key={date} style={{ padding: 8 }}>{dayjs(date).format('DD')}</th>
                            ))}

                            <th style={{ padding: 8 }}>Total</th>
                        </tr>
                    </thead>
                    <tbody>
                        {employees && employees.length > 0 ? (
                            employees.map(emp => {
                                let total = 0;
                                const cells = monthDates.map(date => {
                                    const leaveType = leaveMap[`${emp}-${date}`];

                                    const { code, bgColor } = getLeaveColorCode(leaveType);
                                    if (leaveType) total++;

                                    return (
                                        <td
                                            key={date}
                                            style={{
                                                backgroundColor: leaveType ? bgColor : '#fff',
                                                color: leaveType ? '#fff' : '#000',
                                            }}
                                        >
                                            {leaveType ? code : ''}
                                        </td>
                                    );
                                });

                                return (
                                    <tr key={emp}>
                                        <td className="leave-tracker-employee-cell">
                                            {emp}
                                        </td>
                                        {cells}
                                        <td className="leave-tracker-employee-cell">
                                            {total}
                                        </td>
                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan={monthDates.length + 2} className="no-data-message"> No Data Available.</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            )}
        </div>
    );
};

export default LeaveTracker;
